/**
 * web-cli.ts — root-access terminal · Phase CG (flagship).
 *
 * This is the framework's primary developer surface. Categorised help,
 * interactive tutorial, 50+ commands, autocomplete, history, command
 * cards with liquid-glass DNA, real CKO envelopes, real credits, real
 * agents — all wired to the live api-core.
 *
 * Public surface (unchanged): mountCli, open, close, toggle, Cli.
 */

import { ZeqondClock } from "../orb/zeqond-clock.js";
import { getSlug } from "../agent/agent-rail-bridge.js";
import { commandNames } from "./help.js";
import { closest, esc, CATEGORY_COLOR, type Category } from "./formatters.js";
import { suggest, slashFilter, type SlashEntry } from "./autocomplete.js";
import { dispatch, runtime, register, makeCtx, type Ctx, type CommandResult } from "./commands.js";
import { renderWelcome, welcomeProbe } from "./welcome.js";

const CLI_ID = "zeq-cli";
const STYLE_ID = "zeq-cli-style";
const HISTORY_KEY = "zeq.cli.history";
const HIST_MAX = 50;

let mounted = false;
let cliEl: HTMLElement | null = null;
let feedEl: HTMLElement | null = null;
let inputEl: HTMLInputElement | null = null;
let history: string[] = [];
let histIdx = -1;
let welcomeShown = false;
// P21.R2 — panel mode: the terminal embedded as an IDE panel (the Root
// Workbench dock). No app chrome (header/CLOSE), fills its container,
// compact first-print instead of the full welcome banner. One singleton
// either way — the session survives moves between panel and full-screen.
let panelMode = false;
let panelPreferred = false;

export function mountCli(css: string | null = null): HTMLElement {
  if (mounted && cliEl) return cliEl;

  if (!document.getElementById(STYLE_ID)) {
    if (css) {
      const style = document.createElement("style");
      style.id = STYLE_ID;
      style.setAttribute("data-zeq-locked", "");
      style.textContent = css;
      document.head.appendChild(style);
    } else {
      const link = document.createElement("link");
      link.id = STYLE_ID;
      link.rel = "stylesheet";
      link.href = "/pulse/cli/web-cli.css";
      link.setAttribute("data-zeq-locked", "");
      document.head.appendChild(link);
    }
  }

  const slug = getSlug() ?? "unknown";
  const el = document.createElement("div");
  el.id = CLI_ID;
  el.className = "theme-cryptopunk";
  el.setAttribute("data-zeq-locked", "");
  el.innerHTML = `
    <div class="cli-header">
      <span class="cli-pulse"></span>
      <span class="cli-handle">@${esc(slug)}</span>
      <span class="cli-sub">root · 1.287 Hz · type <code>help</code> · <code>tutorial</code> · <code>hello universe</code></span>
      <span class="cli-spacer"></span>
      <button class="cli-close" id="zeq-cli-close" aria-label="Close (Esc)">CLOSE</button>
    </div>
    <div class="cli-feed" id="zeq-cli-feed"></div>
    <div class="cli-suggest" id="zeq-cli-suggest" aria-live="polite"></div>
    <div class="cli-slash-menu" id="zeq-cli-slash" aria-hidden="true" role="listbox"></div>
    <div class="cli-prompt">
      <span class="cli-prefix">@${esc(slug)}$</span>
      <input class="cli-input" id="zeq-cli-input" type="text" autocomplete="off" spellcheck="false" autofocus placeholder="type / for the command menu &middot; type help for the full surface" />
    </div>
  `;
  document.body.appendChild(el);
  cliEl = el;
  feedEl = el.querySelector("#zeq-cli-feed") as HTMLElement;
  inputEl = el.querySelector("#zeq-cli-input") as HTMLInputElement;
  const suggestEl = el.querySelector("#zeq-cli-suggest") as HTMLElement;
  const slashEl = el.querySelector("#zeq-cli-slash") as HTMLElement;

  // ── Slash-menu state ────────────────────────────────────────────────
  // Open when input starts with `/`. ArrowUp/Down navigate, Enter selects,
  // Escape closes. Clicking a row also selects.
  let slashEntries: SlashEntry[] = [];
  let slashIdx = 0;
  let slashOpen = false;

  function isSlashOpen(): boolean { return slashOpen; }
  function closeSlash(): void {
    if (!slashOpen) return;
    slashOpen = false;
    slashEl.classList.remove("open");
    slashEl.setAttribute("aria-hidden", "true");
    slashEl.innerHTML = "";
    slashEntries = [];
    slashIdx = 0;
  }
  function paintSlash(): void {
    if (!slashEntries.length) {
      slashEl.innerHTML =
        '<div class="cli-slash-empty">no commands match — try <code>/</code> alone, or press <code>Esc</code> to dismiss</div>';
      return;
    }
    const rows = slashEntries.map((e, i) => {
      const col = CATEGORY_COLOR[e.category as Category] || "#9ca3af";
      const sig = e.name + (e.args ? ' <span class="cli-slash-args">' + esc(e.args) + '</span>' : "");
      return (
        '<div class="cli-slash-row' + (i === slashIdx ? " active" : "") + '" data-i="' + i + '" role="option" aria-selected="' + (i === slashIdx) + '">' +
          '<span class="cli-slash-cat" style="color:' + col + ';border-color:' + col + ';">' + esc(e.category) + '</span>' +
          '<span class="cli-slash-name">' + sig + '</span>' +
          '<span class="cli-slash-desc">' + esc(e.blurb) + '</span>' +
        '</div>'
      );
    }).join("");
    const foot =
      '<div class="cli-slash-foot">' +
        '<span>↑↓ navigate</span>' +
        '<span><kbd>Enter</kbd> fill</span>' +
        '<span><kbd>Tab</kbd> autocomplete</span>' +
        '<span><kbd>Esc</kbd> close</span>' +
        '<span class="cli-slash-foot-right">' + slashEntries.length + ' command' + (slashEntries.length === 1 ? "" : "s") + '</span>' +
      '</div>';
    slashEl.innerHTML = rows + foot;
    // Re-scroll active row into view
    const active = slashEl.querySelector(".cli-slash-row.active") as HTMLElement | null;
    if (active) active.scrollIntoView({ block: "nearest" });
  }
  function refreshSlash(rawValue: string): void {
    slashEntries = slashFilter(rawValue);
    slashIdx = 0;
    paintSlash();
  }
  function openSlash(rawValue: string): void {
    slashOpen = true;
    slashEl.classList.add("open");
    slashEl.setAttribute("aria-hidden", "false");
    refreshSlash(rawValue);
  }
  function moveSlash(delta: number): void {
    if (!slashEntries.length) return;
    slashIdx = (slashIdx + delta + slashEntries.length) % slashEntries.length;
    paintSlash();
  }
  function selectSlash(): void {
    if (!slashEntries.length || !inputEl) return;
    const e = slashEntries[slashIdx];
    // Fill input with the command, no leading slash, with a trailing space
    // so the user can immediately type args or hit Enter to run.
    inputEl.value = e.name + " ";
    closeSlash();
    inputEl.focus();
  }
  slashEl.addEventListener("click", (ev) => {
    const t = (ev.target as HTMLElement).closest(".cli-slash-row") as HTMLElement | null;
    if (!t || !inputEl) return;
    const i = parseInt(t.getAttribute("data-i") || "0", 10);
    if (i >= 0 && i < slashEntries.length) {
      slashIdx = i;
      selectSlash();
    }
  });

  el.querySelector("#zeq-cli-close")?.addEventListener("click", close);

  // Live input listener — drives the slash menu in/out
  inputEl.addEventListener("input", () => {
    if (!inputEl) return;
    const v = inputEl.value;
    if (v.startsWith("/")) {
      if (!slashOpen) openSlash(v);
      else refreshSlash(v);
    } else if (slashOpen) {
      closeSlash();
    }
  });

  inputEl.addEventListener("keydown", (ev) => {
    if (!inputEl) return;

    // Slash-menu-active shortcuts win first
    if (isSlashOpen()) {
      if (ev.key === "ArrowDown") { ev.preventDefault(); moveSlash(+1); return; }
      if (ev.key === "ArrowUp")   { ev.preventDefault(); moveSlash(-1); return; }
      if (ev.key === "Enter")     { ev.preventDefault(); selectSlash(); return; }
      if (ev.key === "Escape")    { ev.preventDefault(); closeSlash(); return; }
      // Tab inside slash menu also selects the highlighted row
      if (ev.key === "Tab")       { ev.preventDefault(); selectSlash(); return; }
    }

    if (ev.key === "Enter") {
      ev.preventDefault();
      const lineText = inputEl.value;
      inputEl.value = "";
      suggestEl.textContent = "";
      void runCommand(lineText);
    } else if (ev.key === "Tab") {
      ev.preventDefault();
      const { completion, candidates } = suggest(inputEl.value);
      if (completion) {
        inputEl.value = completion;
        suggestEl.textContent = "";
      } else if (candidates.length) {
        suggestEl.textContent = candidates.join(" · ");
      } else {
        suggestEl.textContent = "";
      }
    } else if (ev.key === "ArrowUp") {
      ev.preventDefault();
      if (history.length === 0) return;
      histIdx = Math.min(history.length - 1, histIdx + 1);
      inputEl.value = history[history.length - 1 - histIdx];
    } else if (ev.key === "ArrowDown") {
      ev.preventDefault();
      histIdx = Math.max(-1, histIdx - 1);
      inputEl.value = histIdx < 0 ? "" : history[history.length - 1 - histIdx];
    } else if (ev.key === "Escape") {
      suggestEl.textContent = "";
    }
  });

  document.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape" && el.classList.contains("open") && !isSlashOpen()) close();
  });
  loadHistory();

  // First-mount welcome — full banner in the app view, one compact line
  // when the first mount is the IDE panel (a 240px dock has no room for
  // the banner; `version` reprints the constants any time).
  if (!welcomeShown) {
    welcomeShown = true;
    if (panelPreferred) {
      const l = document.createElement("div");
      l.className = "cli-line ok";
      l.textContent = "root terminal · @" + slug + " · type help · tutorial · or tap a chip above";
      feedEl.appendChild(l);
    } else {
      feedEl.appendChild(renderWelcome());
    }
    void welcomeProbe().then((p) => {
      if (!p.healthy && feedEl) {
        const w = document.createElement("div");
        w.className = "cli-line err";
        w.textContent = "api-core unreachable — commands will degrade gracefully where possible.";
        feedEl.appendChild(w);
      }
    });
  }

  mounted = true;
  return el;
}

export function open(): void {
  if (!mounted) mountCli();
  if (!cliEl) return;
  cliEl.classList.add("open");
  setTimeout(() => inputEl?.focus(), 50);
}

export function close(): void {
  if (!cliEl) return;
  cliEl.classList.remove("open");
}

export function toggle(): void {
  if (!mounted) mountCli();
  if (!cliEl) return;
  if (cliEl.classList.contains("open")) close();
  else open();
}

// ─── P21.R2 — panel mode (the IDE dock) ────────────────────────────────

/**
 * Mount the terminal as an embedded panel inside `host` — scrollback +
 * prompt only, no header chrome, fills the container. Same singleton as
 * the full-screen view: history and scrollback survive the move.
 */
export function mountPanel(host: HTMLElement): HTMLElement {
  panelPreferred = true;
  const el = mountCli();
  setPanelMode(true);
  if (el.parentElement !== host) host.appendChild(el);
  el.classList.add("open");
  return el;
}

/** Flip panel chrome on/off (the full-screen pane calls setPanelMode(false)). */
export function setPanelMode(on: boolean): void {
  panelMode = !!on;
  cliEl?.classList.toggle("panel", panelMode);
}

/**
 * Programmatic command execution — exactly as if the developer typed it
 * and pressed enter (prints the user line, runs, lands in history).
 */
export function run(cmd: string): Promise<void> {
  if (!mounted) mountCli();
  return runCommand(String(cmd ?? ""));
}

// ─── Runner ────────────────────────────────────────────────────────────

async function runCommand(lineText: string): Promise<void> {
  const cmd = lineText.trim();
  if (!cmd) return;
  if (!feedEl) return;
  printUser(cmd);
  pushHistory(cmd);

  const parts = cmd.split(/\s+/);
  const head = parts[0].toLowerCase();
  // BUILD-A — strip the global --json/-j flag; machine mode also honours the
  // persistent `json on` toggle (runtime.json).
  const jsonMode = parts.includes("--json") || parts.includes("-j") || runtime.json === true;
  const args = parts.slice(1).filter(a => a !== "--json" && a !== "-j");

  const ctx: Ctx = makeCtx({
    feed: feedEl,
    append: (el) => { feedEl?.appendChild(el); feedEl && (feedEl.scrollTop = feedEl.scrollHeight); },
    print: (text, kind = "ok") => {
      const l = document.createElement("div");
      l.className = "cli-line " + kind;
      l.textContent = text;
      feedEl?.appendChild(l);
      feedEl && (feedEl.scrollTop = feedEl.scrollHeight);
    },
    args,
    raw: cmd,
    json: jsonMode,
  });

  const handled = await dispatch(ctx, head);
  if (!handled) {
    const c = closest(head, commandNames());
    ctx.fail('unknown: ' + head + (c ? ' — did you mean `' + c + '`?' : '. type `help` to list commands.'), 127);
  }
}

/**
 * BUILD-A — headless runner. Executes a command with no DOM rendering and
 * returns the structured CommandResult plus captured text output. This is the
 * stable, machine-readable contract an application (or a native CLI) builds
 * on: `const r = await window.zeqCli.runJson("compute NM19 mass=5 acceleration=2")`.
 * Runs in the browser context (uses the page session/credentials); a Node
 * port follows once identity/origin are decoupled from `window` (BUILD-G).
 */
export async function runHeadless(
  cmd: string,
  opts: { json?: boolean } = {},
): Promise<CommandResult & { output: string }> {
  if (!mounted) mountCli();
  const lineText = String(cmd ?? "").trim();
  const lines: string[] = [];
  const sink = (typeof document !== "undefined") ? document.createElement("div") : (null as unknown as HTMLElement);
  const parts = lineText.split(/\s+/);
  const head = (parts[0] || "").toLowerCase();
  const args = parts.slice(1).filter(a => a !== "--json" && a !== "-j");
  const ctx = makeCtx({
    feed: sink,
    append: (el) => { if (sink) sink.appendChild(el); lines.push((el.textContent || "").trim()); },
    print: (text) => { lines.push(text); },
    args,
    raw: lineText,
    json: opts.json !== false, // headless defaults to machine-readable
  });
  if (lineText) {
    const handled = await dispatch(ctx, head);
    if (!handled) ctx.fail("unknown command: " + head, 127);
  } else {
    ctx.fail("empty command", 2);
  }
  return {
    ok: ctx.result.ok,
    exitCode: ctx.result.exitCode,
    command: ctx.result.command,
    data: ctx.result.data ?? null,
    error: ctx.result.error,
    output: lines.join("\n"),
  };
}

function printUser(text: string): void {
  if (!feedEl) return;
  const l = document.createElement("div");
  l.className = "cli-line user";
  l.textContent = text;
  feedEl.appendChild(l);
  feedEl.scrollTop = feedEl.scrollHeight;
}

// ─── History ───────────────────────────────────────────────────────────

function pushHistory(cmd: string): void {
  history.push(cmd);
  if (history.length > HIST_MAX) history = history.slice(-HIST_MAX);
  histIdx = -1;
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(history)); } catch { /* */ }
}
function loadHistory(): void {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (raw) history = JSON.parse(raw) ?? [];
  } catch { history = []; }
}

// Keep ZeqondClock + register imports referenced so esbuild doesn't drop them
// in tree-shake (the commands module registers handlers on import side-effect).
void ZeqondClock;
void register;

export const Cli = { mount: mountCli, open, close, toggle, mountPanel, setPanelMode, run, runJson: runHeadless, runHeadless };
export default Cli;

// BUILD-A — make the headless contract reachable on the global regardless of
// how the host glue assembles window.zeqCli (merge, don't clobber).
if (typeof window !== "undefined") {
  const g = window as unknown as { zeqCli?: Record<string, unknown> };
  g.zeqCli = Object.assign(g.zeqCli || {}, { runJson: runHeadless, runHeadless, run });
}
